import { useState, useMemo } from 'react';
import { Typography, Button } from '@getsynapse/design-system';
import classnames from 'classnames';
import {
  DeleteProgramFlag,
  Program,
  MoreActionsOption,
} from 'utils/customTypes';
import {
  PROJECT_STATUS,
  PROJECT_HEALTH,
  DELETE_PROGRAM_OPTIONS,
  PATHS,
  PROGRAM_MORE_ACTIONS,
  USERPILOT_EVENTS,
} from 'utils/constants';
import intl from 'react-intl-universal';
import MoreActions from 'Organisms/MoreActions/MoreActions';
import LinkProjectsModal from './LinkProjectsModal';
import ProgramInsights from './ProgramInsights';
import moment from 'moment';

import DeleteProgramModal from './DeleteProgramModal';
import { useDispatch, useSelector } from 'react-redux';
import { Userpilot } from 'userpilot';
import { selectUserEmail, selectOrganizationId } from 'state/User/userSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { deleteProgramAction } from 'state/Programs/programsSlice';
import get from 'lodash/get';
import { useHistory } from 'react-router-dom';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import ProgramProjects from './ProgramProjects/ProgramProjects';
import { formatProgramNumber } from 'utils/formatters';
import ArchiveProgramModal from './ArchiveProgramModal';

const ProgramContent = ({
  program,
  userHasWriteAccess,
  userCanManageProgram,
  isReadOnly = false,
  changesDetected,
  onUnarchiveClick,
}: {
  program: Program;
  userHasWriteAccess: boolean;
  /**
   * Permission to use the More Actions menu (Delete/Archive). Unlike
   * `userHasWriteAccess` this stays true for Closed programs (admins/owners),
   * so the menu remains available there — see CGP-111. Defaults to
   * `userHasWriteAccess` when omitted, which is equivalent for non-Closed
   * programs.
   */
  userCanManageProgram?: boolean;
  isReadOnly?: boolean;
  changesDetected: boolean;
  onUnarchiveClick: () => void;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { archivePrograms = false } = useFlags();
  const userEmail = useSelector(selectUserEmail);
  const organizationId = useSelector(selectOrganizationId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  const [allProjectsCompleted, setAllProjectsCompleted] = useState(false);

  useMemo(() => {
    if (program.programProjects && program.programProjects.length > 0) {
      const result = program.programProjects.every(
        (project) => project.status === PROJECT_STATUS.COMPLETED
      );
      setAllProjectsCompleted(result);
    }
  }, [program.programProjects]);

  const projectDetails = useMemo(() => {
    let totalTaskCompleted = 0;
    let totalTaskNotCompleted = 0;
    let projectDataToWorkWith = program.updatedProjects || [];
    const excludedProjectStatuses = [
      PROJECT_STATUS.CANCELED,
      PROJECT_STATUS.COMPLETED,
      PROJECT_STATUS.CLOSED,
    ];

    let projectDetails = {
      totalNumberOfProjects: 0,
      totalNumberOfNewProjects: 0,
      totalNumberOfProjectsInQueue: 0,
      totalNumberOfProjectsOnHold: 0,
      totalNumberOfProjectsCompleted: 0,
      totalNumberOfProjectsInProgress: 0,
      averageTaskCompletionPercentage: 0,
      totalNumberOfProjectsAtRisk: 0,
      totalNumberOfProjectsOverdue: 0,
    };
    projectDetails.totalNumberOfProjects =
      (projectDataToWorkWith && projectDataToWorkWith.length) || 0;
    projectDataToWorkWith &&
      projectDataToWorkWith.forEach((project) => {
        project.status === PROJECT_STATUS.NEW &&
          projectDetails.totalNumberOfNewProjects++;
        project.status === PROJECT_STATUS.IN_PLANNING &&
          projectDetails.totalNumberOfProjectsInQueue++;
        project.status === PROJECT_STATUS.ON_HOLD &&
          projectDetails.totalNumberOfProjectsOnHold++;
        project.status === PROJECT_STATUS.COMPLETED &&
          projectDetails.totalNumberOfProjectsCompleted++;
        project.status === PROJECT_STATUS.IN_PROGRESS &&
          projectDetails.totalNumberOfProjectsInProgress++;
        project.health === PROJECT_HEALTH.AT_RISK &&
          projectDetails.totalNumberOfProjectsAtRisk++;
        totalTaskCompleted += project.number_of_completed_tasks;
        totalTaskNotCompleted += project.number_of_not_completed_tasks;
        if (!excludedProjectStatuses.includes(project.status)) {
          moment(new Date(project.targetCompletionDate)).isBefore(moment()) &&
            projectDetails.totalNumberOfProjectsOverdue++;
        }
      });
    const taskCompletionPercentage =
      program.updatedProjects && program.updatedProjects.length === 0
        ? 0
        : Math.round(
            (totalTaskCompleted /
              (totalTaskCompleted + totalTaskNotCompleted)) *
              100
          );
    projectDetails.averageTaskCompletionPercentage = Number.isFinite(
      taskCompletionPercentage
    )
      ? taskCompletionPercentage
      : 0;
    return projectDetails;
  }, [program.updatedProjects]);

  const deleteProgram = async (
    flag: DeleteProgramFlag,
    newProgramId?: string
  ) => {
    const removedProgram = await dispatch(
      deleteProgramAction({
        programId: program.id,
        flag,
        newProgramId: newProgramId || '',
      })
    );
    if (get(removedProgram, 'payload.id')) {
      Userpilot.track(USERPILOT_EVENTS.PROGRAM_DELETED, {
        email: userEmail,
        companyId: organizationId,
        programId: program.id,
      });
      history.push(PATHS.PROGRAMS_LIST_PAGE);
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            `PROGRAM_PAGE.DELETE_PROGRAM_SUCCESS.${flag}`
          ),
          autoHide: true,
        })
      );
    } else {
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get('PROGRAM_PAGE.DELETE_PROGRAM_ERROR'),
          autoHide: true,
        })
      );
    }
  };

  const handleDeleteProgram = async () => {
    if (program.programProjects.length) {
      setIsDeleteModalOpen(true);
    } else {
      await deleteProgram(DELETE_PROGRAM_OPTIONS.NO_PROJRCTS);
    }
  };

  const canManageProgram = userCanManageProgram ?? userHasWriteAccess;

  const canArchiveProgram =
    archivePrograms && canManageProgram && !program.is_archived;

  const handleSelectOption = (option: MoreActionsOption) => {
    if (option.value === PROGRAM_MORE_ACTIONS.DELETE) {
      if (changesDetected) return; // Don't open modal if there are unsaved changes
      handleDeleteProgram();
    }

    if (option.value === PROGRAM_MORE_ACTIONS.ARCHIVE) {
      setIsArchiveModalOpen(true);
    }

    if (option.value === PROGRAM_MORE_ACTIONS.UNARCHIVE) {
      onUnarchiveClick();
    }
  };

  const moreActionsOptions: MoreActionsOption[] = useMemo(() => {
    if (!canManageProgram) return [];

    const options = [];

    // Delete option first
    options.push({
      value: PROGRAM_MORE_ACTIONS.DELETE,
      label: intl.get('PROGRAM_PAGE.DELETE_PROGRAM'),
      iconName: 'trash',
      iconClassName: 'text-error-darker group-hover:text-error-dark',
      disabled: changesDetected,
      tooltip: changesDetected
        ? {
            text: intl.get('PROGRAM_PAGE.DELETE_PROGRAM_TOOLTIP'),
          }
        : undefined,
      dataCy: 'delete-program-option',
    });

    // Archive/Unarchive option second
    if (canArchiveProgram && archivePrograms) {
      if (program.is_archived) {
        options.push({
          value: PROGRAM_MORE_ACTIONS.UNARCHIVE,
          label: intl.get('PROGRAM_PAGE.UNARCHIVE_PROGRAM_MODAL.TITLE'),
          iconName: 'unarchive',
          dataCy: 'unarchive-program-option',
        });
      } else {
        options.push({
          value: PROGRAM_MORE_ACTIONS.ARCHIVE,
          label: intl.get('PROGRAM_PAGE.ARCHIVE_PROGRAM'),
          iconName: 'archive',
          dataCy: 'archive-program-option',
        });
      }
    }

    return options;
  }, [
    canManageProgram,
    archivePrograms,
    program.is_archived,
    changesDetected,
    canArchiveProgram,
  ]);

  return (
    <>
      <LinkProjectsModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      <ArchiveProgramModal
        isOpen={isArchiveModalOpen}
        setIsOpen={setIsArchiveModalOpen}
        programId={program.id}
      />
      <DeleteProgramModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        program={program}
        deleteProgram={deleteProgram}
      />
      <div
        className={classnames(
          'bg-neutral-white w-full ml-4 border border-neutral-lighter-two relative',
          'border-b-0 rounded-t-lg rounded-b-none overflow-x-auto'
        )}
      >
        <div className='py-2 flex justify-between	items-center pl-6 pr-2'>
          <Typography variant='body2'>
            <span className='font-semibold'>{`${intl.get(
              'PROGRAM_PAGE.ID'
            )} : `}</span>
            {formatProgramNumber(program.program_number)}
          </Typography>
          <div className='flex items-center gap-2'>
            {moreActionsOptions.length > 0 && (
              <MoreActions
                options={moreActionsOptions}
                onSelectOption={handleSelectOption}
              />
            )}
          </div>
        </div>
        <ProgramInsights
          projectDetails={projectDetails}
          allProjectsCompleted={allProjectsCompleted}
        />
        <div className='w-full flex py-6 px-6'>
          <div className='flex flex-col pb-4'>
            <Typography variant='h5'>
              {intl.get('PROGRAM_PAGE.PROJECTS')}
            </Typography>
            <Typography variant='body2' className='text-neutral'>
              {intl.get('PROGRAM_PAGE.PROJECTS_SUB')}
            </Typography>
          </div>
          {userHasWriteAccess && !isReadOnly && (
            <Button
              variant='secondary'
              data-cy='link-projects-button'
              className='h-8 ml-auto'
              onClick={() => setIsModalOpen(true)}
            >
              {intl.get('PROGRAM_PAGE.LINK_PROJECTS')}
            </Button>
          )}
        </div>
        <div className='px-6'>
          <ProgramProjects
            projects={program.updatedProjects || []}
            userHasWriteAccess={userHasWriteAccess}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    </>
  );
};

export default ProgramContent;
